import React, { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Building2, Edit2, Save, X } from "lucide-react";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";

import {
  contractorEntityFormSchema,
  ContractorEntityFormData,
  DEFAULT_CONTRACTOR_ADDRESS,
  Address,
  ContractorEntity,
} from "@/types/contractor-entity";
import { ContractorCountryProfile, USOnboardingRequest } from "@/types/country";
import {
  getCountryFieldConfig,
  getCountryCodeFromEntity,
  usesVATRegistration,
  usesW9Acknowledgment,
  usesSalesTaxRegistration,
  getTaxRegistrationFieldName,
  getTaxNumberFieldName,
} from "@/utils/contractorEntityCountryUtils";
import DashboardContainer from "../layout/DashboardContainer";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { CountryService } from "@/services/countryService";

interface ContractorEntityDetailsProps {
  entity: ContractorEntity;
  onUpdate: () => void;
}

export default function ContractorEntityDetails({
  entity,
  onUpdate,
}: ContractorEntityDetailsProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryProfile, setCountryProfile] = useState<ContractorCountryProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  
  // Get country code and field configuration
  const countryCode = useMemo(() => getCountryCodeFromEntity(entity), [entity]);
  const fieldConfig = useMemo(() => getCountryFieldConfig(countryCode), [countryCode]);

  // Merge entity data with country profile data (especially W-9 fields for US)
  const mergedEntity = useMemo(() => {
    const merged = { ...entity };
    
    // For US entities, merge W-9 data from country profile
    if (countryCode === 'US' && countryProfile?.additionalData) {
      merged.w9Acknowledged = countryProfile.additionalData.w9_acknowledged || false;
      merged.taxId = countryProfile.taxIdentifier || '';
    }
    
    return merged;
  }, [entity, countryProfile, countryCode]);

  // Fetch country profile data on mount and after successful saves
  useEffect(() => {
    const fetchCountryProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const profile = await CountryService.getCountryProfile();
        setCountryProfile(profile);
        console.log("🌍 Fetched country profile:", profile);
      } catch (error) {
        console.error('Error fetching country profile:', error);
        setCountryProfile(null);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchCountryProfile();
  }, []);

  const form = useForm<ContractorEntityFormData>({
    resolver: zodResolver(contractorEntityFormSchema),
    defaultValues: {
      hasExistingEntity: true,
      entityDetails: {
        businessName: entity.businessName,
        registrationNumber: entity.registrationNumber || "",
        proposedBusinessName: "",
        // Set the appropriate tax registration fields based on country
        vatRegistered: usesVATRegistration(countryCode) ? entity.vatRegistered : false,
        vatNumber: usesVATRegistration(countryCode) ? (entity.vatNumber || "") : "",
        w9Acknowledged: usesW9Acknowledgment(countryCode) ? (mergedEntity.w9Acknowledged || false) : false,
        taxId: usesW9Acknowledgment(countryCode) ? (mergedEntity.taxId || "") : "",
        salesTaxRegistered: usesSalesTaxRegistration(countryCode) ? (entity.salesTaxRegistered || false) : false,
        salesTaxNumber: usesSalesTaxRegistration(countryCode) ? (entity.salesTaxNumber || "") : "",
        taxRegistered: (!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode)) ? (entity.taxRegistered || false) : false,
        taxNumber: (!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode)) ? (entity.taxNumber || "") : "",
        yearlyTurnover: typeof entity.yearlyTurnover === 'string' ? Number(entity.yearlyTurnover) : entity.yearlyTurnover || undefined,
        registeredAddress: {
          ...(entity.registeredAddress || DEFAULT_CONTRACTOR_ADDRESS),
          // Ensure country code is preserved
          country_code: entity.registeredAddress?.country_code || countryCode,
        },
        tradingAddressDifferent: !!entity.tradingAddress,
        tradingAddress: entity.tradingAddress ? {
          ...entity.tradingAddress,
          country_code: entity.tradingAddress?.country_code || entity.registeredAddress?.country_code || countryCode,
        } : {
          ...DEFAULT_CONTRACTOR_ADDRESS,
          country_code: countryCode,
        },
        bankDetails: entity.bankDetails || "",
      },
      contactDetails: {
        fullName: "Business Contact Person",
        phone: "+44 1234 567890",
      },
    },
    mode: "onChange",
  });

  // Reset form when country profile loads to include W-9 data
  useEffect(() => {
    if (!isLoadingProfile) {
      form.reset({
        hasExistingEntity: true,
        entityDetails: {
          businessName: entity.businessName,
          registrationNumber: entity.registrationNumber || "",
          proposedBusinessName: "",
          // Set the appropriate tax registration fields based on country
          vatRegistered: usesVATRegistration(countryCode) ? entity.vatRegistered : false,
          vatNumber: usesVATRegistration(countryCode) ? (entity.vatNumber || "") : "",
          w9Acknowledged: usesW9Acknowledgment(countryCode) ? (mergedEntity.w9Acknowledged || false) : false,
          taxId: usesW9Acknowledgment(countryCode) ? (mergedEntity.taxId || "") : "",
          salesTaxRegistered: usesSalesTaxRegistration(countryCode) ? (entity.salesTaxRegistered || false) : false,
          salesTaxNumber: usesSalesTaxRegistration(countryCode) ? (entity.salesTaxNumber || "") : "",
          taxRegistered: (!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode)) ? (entity.taxRegistered || false) : false,
          taxNumber: (!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode)) ? (entity.taxNumber || "") : "",
          yearlyTurnover: typeof entity.yearlyTurnover === 'string' ? Number(entity.yearlyTurnover) : entity.yearlyTurnover || undefined,
          registeredAddress: {
            ...(entity.registeredAddress || DEFAULT_CONTRACTOR_ADDRESS),
            // Ensure country code is preserved
            country_code: entity.registeredAddress?.country_code || countryCode,
          },
          tradingAddressDifferent: !!entity.tradingAddress,
          tradingAddress: entity.tradingAddress ? {
            ...entity.tradingAddress,
            country_code: entity.tradingAddress?.country_code || entity.registeredAddress?.country_code || countryCode,
          } : {
            ...DEFAULT_CONTRACTOR_ADDRESS,
            country_code: countryCode,
          },
          bankDetails: entity.bankDetails || "",
        },
        contactDetails: {
          fullName: "Business Contact Person",
          phone: "+44 1234 567890",
        },
      });
    }
  }, [mergedEntity, isLoadingProfile, form, entity, countryCode]);

  const onSubmit = async (data: ContractorEntityFormData) => {
    console.log("📋 Full form data:", JSON.stringify(data, null, 2));
    console.log("🇺🇸 US-specific form data:", {
      w9Acknowledged: data.entityDetails.w9Acknowledged,
      taxId: data.entityDetails.taxId,
      countryCode: countryCode
    });
    setIsSubmitting(true);
    try {
      const submitData = {
        hasExistingEntity: true,
        existingEntityDetails: {
          businessName: data.entityDetails.businessName,
          registrationNumber: data.entityDetails.registrationNumber,
          // Only include VAT fields for VAT countries (UK, etc.)
          vatRegistered: usesVATRegistration(countryCode) ? data.entityDetails.vatRegistered : false,
          vatNumber: usesVATRegistration(countryCode) && data.entityDetails.vatRegistered
            ? data.entityDetails.vatNumber
            : undefined,
          // Only include yearly turnover for VAT countries that need it
          yearlyTurnover: usesVATRegistration(countryCode) && data.entityDetails.vatRegistered ? data.entityDetails.yearlyTurnover : undefined,
          // Note: W-9 data (w9Acknowledged, taxId) will be saved separately via country profile
          registeredAddress: {
            ...data.entityDetails.registeredAddress,
            // Preserve the original country code from the entity
            country_code: entity.registeredAddress.country_code || countryCode,
          },
          tradingAddressDifferent: data.entityDetails.tradingAddressDifferent,
          tradingAddress: data.entityDetails.tradingAddressDifferent
            ? {
                ...data.entityDetails.tradingAddress,
                // Preserve the original country code for trading address too
                country_code: entity.tradingAddress?.country_code || entity.registeredAddress.country_code || countryCode,
              }
            : undefined,
          bankDetails: data.entityDetails.bankDetails,
        },
        contactDetails: {
          fullName: data.contactDetails.fullName || "",
          phone: data.contactDetails.phone || "",
        },
      };

      await ApiService.request(EndpointKeys.UPDATE_CONTRACTOR_ENTITY, {
        method: "POST",
        body: submitData,
      });

      // For US entities with W-9 data, use the US onboard endpoint
      // Note: Use form data since entity doesn't store W-9 fields
      if (countryCode === 'US' && data.entityDetails.w9Acknowledged && data.entityDetails.taxId) {
        console.log("🇺🇸 Attempting US onboarding...");
        console.log("Entity ID:", entity.id);
        console.log("Business Name:", data.entityDetails.businessName);
        console.log("Tax ID:", data.entityDetails.taxId);
        console.log("W-9 Acknowledged:", data.entityDetails.w9Acknowledged);
        
        try {
          // Auto-format and determine tax ID type
          const rawTaxId = data.entityDetails.taxId.replace(/\D/g, ''); // Remove all non-digits
          let formattedTaxId: string;
          let taxIdType: 'ein' | 'ssn';
          
          if (rawTaxId.length === 9) {
            // Could be either EIN (XX-XXXXXXX) or SSN (XXX-XX-XXXX)
            // Default to EIN for business entities, but let user's input format decide
            const originalHasDashesAsEIN = /^\d{2}-\d{7}$/.test(data.entityDetails.taxId);
            const originalHasDashesAsSSN = /^\d{3}-\d{2}-\d{4}$/.test(data.entityDetails.taxId);
            
            if (originalHasDashesAsEIN) {
              // User entered in EIN format
              formattedTaxId = data.entityDetails.taxId;
              taxIdType = 'ein';
            } else if (originalHasDashesAsSSN) {
              // User entered in SSN format  
              formattedTaxId = data.entityDetails.taxId;
              taxIdType = 'ssn';
            } else {
              // No dashes - default to EIN format for businesses
              formattedTaxId = `${rawTaxId.slice(0, 2)}-${rawTaxId.slice(2)}`;
              taxIdType = 'ein';
            }
          } else {
            // Invalid length - use as-is and let backend validate
            formattedTaxId = data.entityDetails.taxId;
            taxIdType = 'ein'; // Default
          }
          
          console.log("Tax ID Format Analysis:", { 
            original: data.entityDetails.taxId,
            raw: rawTaxId,
            formatted: formattedTaxId,
            type: taxIdType,
            length: rawTaxId.length
          });
          
          const usOnboardData = {
            contractorId: entity.id?.toString() || '',
            legalName: data.entityDetails.businessName || '',
            einOrSsn: formattedTaxId,
            taxIdType: taxIdType,
            w9Acknowledged: true,
            businessType: 'corporation' as 'individual' | 'llc' | 'corporation' | 'partnership',
            address: {
              line1: [data.entityDetails.registeredAddress.street_number, data.entityDetails.registeredAddress.route].filter(Boolean).join(' '),
              city: data.entityDetails.registeredAddress.locality || '',
              state: data.entityDetails.registeredAddress.state || '',
              zipCode: data.entityDetails.registeredAddress.postal_code || '',
              country: data.entityDetails.registeredAddress.country || 'United States',
            }
          };

          console.log("🚀 US Onboarding Data:", JSON.stringify(usOnboardData, null, 2));

          const result = await CountryService.submitUSOnboarding(usOnboardData);
          
          console.log("✅ US onboarding SUCCESS:", result);
        } catch (profileError: any) {
          console.error("❌ US onboarding FAILED:", profileError);
          console.error("Error details:", {
            status: profileError?.status,
            message: profileError?.message,
            response: profileError?.response,
            data: profileError?.response?.data
          });
          // Don't fail the whole operation if country profile update fails
        }
      } else {
        console.log("⚠️ Skipping US onboarding because:", {
          countryCode,
          w9Acknowledged: data.entityDetails.w9Acknowledged,
          taxId: data.entityDetails.taxId,
          shouldOnboard: countryCode === 'US' && data.entityDetails.w9Acknowledged && data.entityDetails.taxId
        });
      }

      // Always refresh country profile after entity update to get latest W-9 data
      try {
        const updatedProfile = await CountryService.getCountryProfile();
        setCountryProfile(updatedProfile);
        console.log("🔄 Refreshed country profile after entity update:", updatedProfile);
      } catch (refreshError) {
        console.error("Failed to refresh country profile after entity update:", refreshError);
      }

      toast({
        title: "Success",
        description: "Your entity details have been updated successfully.",
      });
      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error("Update error:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          "Failed to update entity details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderViewMode = () => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Business Details</CardTitle>
          <CardDescription>
            Your registered business information ({countryCode})
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
          <Edit2 className="h-4 w-4 mr-2" />
          Edit Details
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">
              Business Name
            </h3>
            <p className="mt-1">{entity.businessName}</p>
          </div>
          {/* Hide registration number for US entities since they use Tax ID instead */}
          {countryCode !== 'US' && entity.registrationNumber && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">
                {fieldConfig.registrationNumberLabel}
              </h3>
              <p className="mt-1">{entity.registrationNumber}</p>
            </div>
          )}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">
              {fieldConfig.taxRegistrationLabel}
            </h3>
            <p className="mt-1">
              {(usesVATRegistration(countryCode) && mergedEntity.vatRegistered) ||
               (usesW9Acknowledgment(countryCode) && mergedEntity.w9Acknowledged) ||
               (usesSalesTaxRegistration(countryCode) && mergedEntity.salesTaxRegistered) ||
               (!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode) && mergedEntity.taxRegistered)
                ? "Yes" : "No"}
            </p>
          </div>
          {((usesVATRegistration(countryCode) && mergedEntity.vatRegistered && mergedEntity.vatNumber) ||
            (usesW9Acknowledgment(countryCode) && mergedEntity.w9Acknowledged && mergedEntity.taxId) ||
            (usesSalesTaxRegistration(countryCode) && mergedEntity.salesTaxRegistered && mergedEntity.salesTaxNumber) ||
            (!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode) && mergedEntity.taxRegistered && mergedEntity.taxNumber)) && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">
                {fieldConfig.taxNumberLabel}
              </h3>
              <p className="mt-1">
                {usesVATRegistration(countryCode) ? mergedEntity.vatNumber :
                 usesW9Acknowledgment(countryCode) ? mergedEntity.taxId :
                 usesSalesTaxRegistration(countryCode) ? mergedEntity.salesTaxNumber :
                 mergedEntity.taxNumber}
              </p>
            </div>
          )}
          {((usesVATRegistration(countryCode) && mergedEntity.vatRegistered) ||
            (usesSalesTaxRegistration(countryCode) && mergedEntity.salesTaxRegistered) ||
            (!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode) && mergedEntity.taxRegistered)) && entity.yearlyTurnover && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">
                {fieldConfig.revenueLabel.replace(/\s*\([^)]*\)/, '')} {/* Remove currency from label for display */}
              </h3>
              <p className="mt-1">{fieldConfig.currencySymbol}{entity.yearlyTurnover.toLocaleString()}</p>
            </div>
          )}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">
              Registered Address
            </h3>
            <p className="mt-1">
              {[
                entity.registeredAddress.street_number,
                entity.registeredAddress.route,
              ]
                .filter(Boolean)
                .join(" ")}
              {entity.registeredAddress.locality && (
                <>
                  <br />
                  {entity.registeredAddress.locality}
                </>
              )}
              {entity.registeredAddress.state && (
                <>
                  <br />
                  {entity.registeredAddress.state}
                </>
              )}
              {entity.registeredAddress.postal_code && (
                <>
                  <br />
                  {entity.registeredAddress.postal_code}
                </>
              )}
              {entity.registeredAddress.country && (
                <>
                  <br />
                  {entity.registeredAddress.country}
                </>
              )}
            </p>
          </div>
          {entity.tradingAddress && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">
                Trading Address
              </h3>
              <p className="mt-1">
                {[
                  entity.tradingAddress.street_number,
                  entity.tradingAddress.route,
                ]
                  .filter(Boolean)
                  .join(" ")}
                {entity.tradingAddress.locality && (
                  <>
                    <br />
                    {entity.tradingAddress.locality}
                  </>
                )}
                {entity.tradingAddress.state && (
                  <>
                    <br />
                    {entity.tradingAddress.state}
                  </>
                )}
                {entity.tradingAddress.postal_code && (
                  <>
                    <br />
                    {entity.tradingAddress.postal_code}
                  </>
                )}
                {entity.tradingAddress.country && (
                  <>
                    <br />
                    {entity.tradingAddress.country}
                  </>
                )}
              </p>
            </div>
          )}
          {entity.bankDetails && (
            <div className="col-span-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {fieldConfig.bankDetailsLabel}
              </h3>
              <div className="mt-1 whitespace-pre-wrap">{entity.bankDetails}</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderEditMode = () => (
    <Form {...form}>
      <form 
        onSubmit={(e) => {
          console.log("Form submit event triggered");
          console.log("Form errors:", form.formState.errors);
          console.log("Form is valid:", form.formState.isValid);
          form.handleSubmit(onSubmit)(e);
        }} 
        className="space-y-8"
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Edit Business Details</CardTitle>
              <CardDescription>
                Update your business information
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(false)}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="entityDetails.businessName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter your registered business name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Hide registration number field for US entities since they use Tax ID instead */}
            {countryCode !== 'US' && (
              <FormField
                control={form.control}
                name="entityDetails.registrationNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{fieldConfig.registrationNumberLabel}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={fieldConfig.registrationNumberPlaceholder} />
                    </FormControl>
                    <FormDescription>
                      {fieldConfig.registrationNumberDescription}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Tax Registration Field - renders the appropriate field based on country */}
            {usesVATRegistration(countryCode) && (
              <FormField
                control={form.control}
                name="entityDetails.vatRegistered"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{fieldConfig.taxRegistrationLabel}</FormLabel>
                      <FormDescription>
                        {fieldConfig.taxRegistrationDescription}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {usesW9Acknowledgment(countryCode) && (
              <FormField
                control={form.control}
                name="entityDetails.w9Acknowledged"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{fieldConfig.taxRegistrationLabel}</FormLabel>
                      <FormDescription>
                        {fieldConfig.taxRegistrationDescription}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {usesSalesTaxRegistration(countryCode) && (
              <FormField
                control={form.control}
                name="entityDetails.salesTaxRegistered"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{fieldConfig.taxRegistrationLabel}</FormLabel>
                      <FormDescription>
                        {fieldConfig.taxRegistrationDescription}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode) && (
              <FormField
                control={form.control}
                name="entityDetails.taxRegistered"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{fieldConfig.taxRegistrationLabel}</FormLabel>
                      <FormDescription>
                        {fieldConfig.taxRegistrationDescription}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {/* Show tax-related fields when appropriate tax registration is enabled */}
            {((usesVATRegistration(countryCode) && form.watch("entityDetails.vatRegistered")) ||
              (usesSalesTaxRegistration(countryCode) && form.watch("entityDetails.salesTaxRegistered")) ||
              (!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode) && form.watch("entityDetails.taxRegistered"))) && (
              <>
                <FormField
                  control={form.control}
                  name="entityDetails.yearlyTurnover"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{fieldConfig.revenueLabel}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Enter your revenue"
                          onChange={(e) =>
                            field.onChange(
                              e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            )
                          }
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormDescription>
                        {fieldConfig.revenueDescription}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Render the appropriate tax number field */}
                {usesVATRegistration(countryCode) && (
                  <FormField
                    control={form.control}
                    name="entityDetails.vatNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{fieldConfig.taxNumberLabel}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={fieldConfig.taxNumberPlaceholder}
                          />
                        </FormControl>
                        <FormDescription>
                          {fieldConfig.taxNumberDescription}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {usesSalesTaxRegistration(countryCode) && (
                  <FormField
                    control={form.control}
                    name="entityDetails.salesTaxNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{fieldConfig.taxNumberLabel}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={fieldConfig.taxNumberPlaceholder}
                          />
                        </FormControl>
                        <FormDescription>
                          {fieldConfig.taxNumberDescription}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {!usesVATRegistration(countryCode) && !usesW9Acknowledgment(countryCode) && !usesSalesTaxRegistration(countryCode) && (
                  <FormField
                    control={form.control}
                    name="entityDetails.taxNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{fieldConfig.taxNumberLabel}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={fieldConfig.taxNumberPlaceholder}
                          />
                        </FormControl>
                        <FormDescription>
                          {fieldConfig.taxNumberDescription}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}

            {/* Separate section for W-9 tax ID field */}
            {usesW9Acknowledgment(countryCode) && form.watch("entityDetails.w9Acknowledged") && (
              <FormField
                control={form.control}
                name="entityDetails.taxId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{fieldConfig.taxNumberLabel}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={fieldConfig.taxNumberPlaceholder}
                      />
                    </FormControl>
                    <FormDescription>
                      {fieldConfig.taxNumberDescription}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="entityDetails.registeredAddress.street_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{fieldConfig.addressLabels.streetNumber}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={fieldConfig.addressPlaceholders.streetNumber} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="entityDetails.registeredAddress.route"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{fieldConfig.addressLabels.streetName}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={fieldConfig.addressPlaceholders.streetName} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="entityDetails.registeredAddress.locality"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{fieldConfig.addressLabels.city}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={fieldConfig.addressPlaceholders.city} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="entityDetails.registeredAddress.state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{fieldConfig.addressLabels.state}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={fieldConfig.addressPlaceholders.state} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="entityDetails.registeredAddress.postal_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{fieldConfig.addressLabels.postalCode}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={fieldConfig.addressPlaceholders.postalCode} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="entityDetails.registeredAddress.country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{fieldConfig.addressLabels.country}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={fieldConfig.addressPlaceholders.country} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="entityDetails.tradingAddressDifferent"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Trading address is different from registered address
                    </FormLabel>
                    <FormDescription>
                      Check this if your business operates from a different
                      address
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {form.watch("entityDetails.tradingAddressDifferent") && (
              <>
                <FormField
                  control={form.control}
                  name="entityDetails.tradingAddress.street_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{fieldConfig.addressLabels.streetNumber}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={fieldConfig.addressPlaceholders.streetNumber} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="entityDetails.tradingAddress.route"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{fieldConfig.addressLabels.streetName}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={fieldConfig.addressPlaceholders.streetName} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="entityDetails.tradingAddress.locality"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{fieldConfig.addressLabels.city}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={fieldConfig.addressPlaceholders.city} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="entityDetails.tradingAddress.state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{fieldConfig.addressLabels.state}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={fieldConfig.addressPlaceholders.state} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="entityDetails.tradingAddress.postal_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{fieldConfig.addressLabels.postalCode}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={fieldConfig.addressPlaceholders.postalCode} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="entityDetails.tradingAddress.country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{fieldConfig.addressLabels.country}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={fieldConfig.addressPlaceholders.country} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="entityDetails.bankDetails"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{fieldConfig.bankDetailsLabel}</FormLabel>
                                      <FormControl>
                      <textarea
                        {...field}
                        placeholder={fieldConfig.bankDetailsPlaceholder}
                        className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        rows={4}
                      />
                    </FormControl>
                    <FormDescription>
                      {fieldConfig.bankDetailsDescription}
                    </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Save className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );

  return (
    <DashboardContainer title="Business Details">
      {isEditing ? renderEditMode() : renderViewMode()}
    </DashboardContainer>
  );
}
